import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import { BASE_URL } from '../../redux/baseQuery';
import { loadMapKit } from '../../webapp/mapkit';
import {
  addGatherTiles,
  gatherPin,
  gatherClusterIcon,
  scatterColocated,
  MapEvent,
} from './mapStyle';
import './map.css';

const BRAND = '#9B5DE5';

const isMappable = (e: MapEvent): boolean =>
  e.status === 'upcoming' &&
  (!e.endDate || new Date(e.endDate).getTime() >= Date.now()) &&
  typeof e.location?.latitude === 'number' &&
  typeof e.location?.longitude === 'number';

const openEvent = (id: string): void => {
  window.location.href = `/event/${id}`;
};

/* Apple Maps (MapKit JS) */
const AppleMap = ({ events }: { events: MapEvent[] }): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const mapRef = useRef<any>(null);

  useEffect(() => {
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loadMapKit().then((mapkit: any) => {
      if (disposed || !mapkit || !ref.current || mapRef.current) return;
      const map = new mapkit.Map(ref.current, {
        colorScheme: mapkit.Map.ColorSchemes.Dark,
        showsCompass: mapkit.FeatureVisibility.Hidden,
        showsScale: mapkit.FeatureVisibility.Hidden,
      });
      mapRef.current = map;
      map.annotationForCluster = (c: { color: string }) => {
        c.color = BRAND;
        return c;
      };
      const annotations = scatterColocated(events).map((p) => {
        const a = new mapkit.MarkerAnnotation(new mapkit.Coordinate(p.lat, p.lng), {
          color: BRAND,
          title: p.event.name,
          clusteringIdentifier: 'events',
        });
        a.data = { id: p.event.id };
        return a;
      });
      map.showItems(annotations);
      map.addEventListener('select', (ev: { annotation?: { data?: { id?: string } } }) => {
        const id = ev.annotation?.data?.id;
        if (id) openEvent(id);
      });
    });
    return () => {
      disposed = true;
      if (mapRef.current) {
        mapRef.current.destroy();
        mapRef.current = null;
      }
    };
  }, [events]);

  return <div ref={ref} className="evmap" />;
};

/* Leaflet fallback */
const LeafletMap = ({ events }: { events: MapEvent[] }): React.JSX.Element => {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { center: [48.6, 19.5], zoom: 5, zoomControl: true });
    mapRef.current = map;
    addGatherTiles(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || events.length === 0) return;
    const cluster = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 55,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c) => gatherClusterIcon(c.getChildCount()),
    });
    scatterColocated(events).forEach((p) => {
      const marker = L.marker([p.lat, p.lng], { icon: gatherPin() });
      marker.bindPopup(
        `<b>${p.event.name.replace(/</g, '&lt;')}</b><br/>${(p.event.location?.city || '').replace(/</g, '&lt;')}`,
      );
      marker.on('click', () => openEvent(p.event.id));
      cluster.addLayer(marker);
    });
    map.addLayer(cluster);
    map.fitBounds(cluster.getBounds().pad(0.2));
    return () => {
      map.removeLayer(cluster);
    };
  }, [events]);

  return <div ref={ref} className="evmap" />;
};

export const EventsMapSection = (): React.JSX.Element | null => {
  const [events, setEvents] = useState<MapEvent[]>([]);
  const [useApple, setUseApple] = useState<boolean | null>(null);

  useEffect(() => {
    loadMapKit().then((m) => setUseApple(!!m));
    fetch(`${BASE_URL}/events?length=200`)
      .then((r) => r.json())
      .then((data: MapEvent[]) => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
  }, []);

  const mappable = useMemo(() => events.filter(isMappable), [events]);

  if (mappable.length === 0) return null;

  return (
    <section className="section" id="map">
      <div className="wrap">
        <h2 style={{ marginBottom: 20 }}>Events on the map</h2>
        {useApple === null ? (
          <div className="evmap" />
        ) : useApple ? (
          <AppleMap events={mappable} />
        ) : (
          <LeafletMap events={mappable} />
        )}
      </div>
    </section>
  );
};
